import { FC } from 'react';
import { Headline, Caption, Button } from '@telegram-apps/telegram-ui';
import { useTranslation } from 'react-i18next';
import { SecretSantaGame } from '@/api/types.ts';
import { TbCheck, TbCopy, TbShare3 } from 'react-icons/tb';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import {haptic} from "@/helpers/haptic.ts";

interface ShareGameDialogProps {
  game: SecretSantaGame;
  onDone: () => void;
}

export const ShareGameDialog: FC<ShareGameDialogProps> = ({ game, onDone }) => {
  const { t } = useTranslation();
  const { showSnackbar } = useNavigation();

  const gameLink = `${import.meta.env.VITE_MINIAPP_URL}?startapp=-s${game.short_id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(gameLink);
      haptic.notification('success');
      showSnackbar('✅', 'secretSanta.share.linkCopied');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    }
  };

  const handleShare = async () => {
    try {
      haptic.impact('light');
      const { shareLink } = await import('@/helpers/shareLink');
      await shareLink(gameLink);
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
          <TbCheck size={40} color="white" />
        </div>
        <Headline weight="2">{t('secretSanta.share.title')}</Headline>
        <Caption className="text-color-hint">{t('secretSanta.share.description')}</Caption>
      </div>

      <div className="p-4 bg-section rounded-lg">
        <Caption className="break-all">{gameLink}</Caption>
      </div>

      <div className="flex flex-col gap-3">
        <Button stretched={true} onClick={handleCopyLink} before={<TbCopy size={20} />} mode="filled">
          {t('secretSanta.share.copyLink')}
        </Button>
        <Button stretched={true} onClick={handleShare} before={<TbShare3 size={20} />} mode="gray">
          {t('secretSanta.share.shareVia')}
        </Button>
        <Button stretched={true} onClick={() => {
          onDone();
        }} mode="plain">
          {t('secretSanta.share.done')}
        </Button>
      </div>
    </div>
  );
};
